/**
 * Veterinarian Recruitment Section for Homepage
 * Professional B2B focused section targeting veterinarians
 * Fetches city data dynamically from API
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, 
  PhoneOff, 
  Calendar, 
  FileText, 
  Video, 
  Heart,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VetRecruitmentSection = () => {
  const [cityData, setCityData] = useState({
    name: "Nîmes",
    total_spots: 10,
    remaining_spots: 7
  });

  useEffect(() => {
    const fetchFeaturedCity = async () => {
      try {
        const response = await fetch(`${API_URL}/api/founder-program/cities/featured`);
        if (response.ok) {
          const data = await response.json();
          setCityData(data);
        }
      } catch (error) {
        console.debug('Using default city data for section');
      }
    };
    fetchFeaturedCity();
  }, []);

  const benefits = [
    { icon: PhoneOff, text: "Moins d'appels téléphoniques" },
    { icon: Calendar, text: "Agenda synchronisé" },
    { icon: FileText, text: "Carnet médical partagé entre confrères" },
    { icon: Video, text: "Téléconsultation intégrée" },
    { icon: Heart, text: "Fidélisation client renforcée" },
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-gray-900 border-t border-b border-slate-200 dark:border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                <Stethoscope className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm uppercase tracking-wide">
                Espace Professionnel
              </span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Vous êtes vétérinaire ?
            </h2>
            
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
              Modernisez votre clinique avec la première plateforme combinant 
              <span className="text-blue-600 dark:text-blue-400 font-semibold"> prise de rendez-vous</span>, 
              <span className="text-blue-600 dark:text-blue-400 font-semibold"> carnet partagé</span> et 
              <span className="text-blue-600 dark:text-blue-400 font-semibold"> téléconsultation</span>.
            </p>
            
            {/* Benefits list */}
            <ul className="space-y-3 mb-8">
              {benefits.map((benefit, index) => (
                <li 
                  key={index}
                  className="flex items-center gap-3 text-slate-700 dark:text-slate-300"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                    <benefit.icon className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span>{benefit.text}</span>
                </li>
              ))}
            </ul>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/veterinaires"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40"
                data-testid="join-founder-program-btn"
              >
                Rejoindre le programme fondateur – {cityData.name}
                <ArrowRight className="w-4 h-4" />
              </Link>
              
              <Link
                to="/veterinaires#demo"
                className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-6 py-3 rounded-xl font-semibold transition-all"
                data-testid="request-demo-btn"
              >
                Demander une démo
              </Link>
            </div>
          </div>
          
          {/* Right content - Stats/Visual */}
          <div className="relative">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-200 dark:border-slate-700">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  Programme Clinique Fondatrice
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Avantages exclusifs pour les premiers partenaires
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">6 mois d'accès privilégié</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">Accompagnement personnalisé</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">Tarifs fondateurs garantis</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">Co-développement des fonctionnalités</span>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Places limitées à <span className="font-bold text-blue-600 dark:text-blue-400">{cityData.name}</span>
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  {cityData.remaining_spots} places restantes sur {cityData.total_spots}
                </p>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-200 dark:bg-blue-800 rounded-full opacity-20 blur-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-green-200 dark:bg-green-800 rounded-full opacity-20 blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VetRecruitmentSection;
